import React, { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react-lite';
import styled, { css } from 'styled-components';
import tinycolor from 'tinycolor2';
import { space } from 'styled-system';

import { KIOSK_PRO_MEDIA, kioskTouchHints } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';
import useUI from 'src/hooks/use-ui';
import { useIsKioskConciergeChromeActive } from 'src/concierge/use-is-kiosk-concierge-chrome-active';
import { useKioskHeaderOffset } from 'src/concierge/use-kiosk-header-offset';
import useCart from 'shared/hooks/use-cart';
import useMediaQuery from 'shared/hooks/use-media-query';
import { ConsumerInfo } from 'src/components/kiosk-pro/kiosk-pro-layout/consumer-info';
import { KIOSK_PRO_PORTRAIT_PANEL_HEIGHT } from 'src/components/kiosk-pro/kiosk-pro-layout/consumer-info/consumer-info.styles';
import { KioskLoginModal } from 'src/components/kiosk-pro/kiosk-login/kiosk-login-modal';
import { getKioskSessionGeneration } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-session-teardown';
import { useKioskProLayout } from 'src/components/kiosk-pro/kiosk-pro-layout/use-kiosk-pro-layout';
import { KioskProNavigation } from 'src/components/kiosk-pro/kiosk-pro-layout/navigation';
import { EndSessionModal } from 'src/components/kiosk-pro/kiosk-pro-layout/end-session-modal';
import { KioskProTouchDiagnostics } from 'src/components/kiosk-pro/touch-diagnostics';
import type { Dispensaries } from 'src/gql/graphql';

type KioskProLayoutProps = {
  dispensary: Dispensaries;
  children: React.ReactNode;
};

export const KioskProLayout = ({ dispensary, children }: KioskProLayoutProps): JSX.Element => {
  const {
    isConsumerPhoneLoginPage,
    startNewSession,
    goToCart,
    kioskProModalLogin,
    createPublicContextSessionLoading,
    isKioskProLoginModalOpen,
    publicLoginSessionId,
    setIsKioskProLoginModalOpen,
    consumerPhoneLoginUrl,
    isEndSessionModalOpen,
    setIsEndSessionModalOpen,
    confirmedStartNewSession,
  } = useKioskProLayout({ dispensary });

  const Cart = useCart();
  const UI = useUI();
  // Lets the concierge bridge start a Kiosk Pro login through the same action as the
  // "Log in" button. The ref keeps the registered callback on the latest closure.
  const kioskProModalLoginRef = useRef(kioskProModalLogin);
  kioskProModalLoginRef.current = kioskProModalLogin;
  // One login at a time per session: repeated bridge requests while one is in flight share it, so
  // they do not create several public sessions. A login started before a session end is not shared
  // with a request made after it (that login is dropped), so the entry records its generation.
  const inFlightLoginRef = useRef<{ generation: number; promise: Promise<void> } | null>(null);
  useEffect(() => {
    UI.setKioskProLoginRequestHandler(() => {
      const generation = getKioskSessionGeneration();
      if (inFlightLoginRef.current?.generation === generation) {
        return inFlightLoginRef.current.promise;
      }
      const entry: { generation: number; promise: Promise<void> } = {
        generation,
        promise: kioskProModalLoginRef.current().finally(() => {
          if (inFlightLoginRef.current === entry) {
            inFlightLoginRef.current = null;
          }
        }),
      };
      inFlightLoginRef.current = entry;
      return entry.promise;
    });
    return () => UI.setKioskProLoginRequestHandler(undefined);
  }, [UI]);
  // Deprecated, but intentionally kept: this force-re-renders the whole layout on a
  // Cart.subtotal change, and that re-render is one of the incidental triggers through which
  // untracked UI reads below (e.g. isKioskProLoginModalOpen) reach the DOM. Removing it is a
  // behaviour change -- see "Noticed, out of scope" in the PR description.
  const orderSubtotal = useObserver(() => Cart.subtotal);
  const disableFasterCheckout = dispensary.orderTypesConfigV2?.kiosk?.disableFasterCheckout ?? false;

  // Same route + gate as the chrome theme (the footer in CoreMenuLayout uses the same latch).
  const isOnConciergeRoute = useObserver(() => UI.isConciergeActive);
  const isConciergeBox = useIsKioskConciergeChromeActive(dispensary, isOnConciergeRoute);
  // Portrait only: in landscape the nav is the full-height sidebar, and its height must not reach
  // the portrait offset after a rotation.
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const navHeight = useKioskHeaderOffset(isConciergeBox && isPortrait, '[data-testid="kiosk-pro-navigation"]');

  return (
    <TwoColumn padding='0'>
      <TwoColumnSidebar>
        <KioskProNavigation />
      </TwoColumnSidebar>

      <TwoColumnContent
        $isConciergeBox={isConciergeBox}
        // Before the first measure the portrait fallbacks apply.
        style={navHeight ? ({ '--kiosk-pro-nav-height': `${navHeight}px` } as React.CSSProperties) : undefined}
      >
        {children}
      </TwoColumnContent>

      {!isConsumerPhoneLoginPage && (
        <ConsumerInfo
          createPublicContextSessionLoading={createPublicContextSessionLoading}
          goToCart={goToCart}
          kioskProModalLogin={kioskProModalLogin}
          orderSubTotal={orderSubtotal}
          startNewSession={startNewSession}
        />
      )}

      {!disableFasterCheckout && isKioskProLoginModalOpen && !!publicLoginSessionId && (
        <KioskLoginModal
          isOpen={isKioskProLoginModalOpen}
          handleClose={() => setIsKioskProLoginModalOpen(false)}
          consumerPhoneLoginUrl={consumerPhoneLoginUrl}
          setupUrl={`/kiosks/${String(dispensary.cName ?? '')}/setup`}
        />
      )}

      <EndSessionModal
        startNewSession={() => void confirmedStartNewSession()}
        isOpen={isEndSessionModalOpen}
        handleCloseModal={() => setIsEndSessionModalOpen(false)}
      />

      <KioskProTouchDiagnostics dispensary={dispensary} />
    </TwoColumn>
  );
};

const TwoColumn = styled.div`
  display: flex;
  padding: 0;

  ${space}

  a, button {
    ${kioskTouchHints}
  }

  @media (orientation: portrait) {
    flex-direction: column;
  }
`;

const TwoColumnSidebar = styled.div`
  width: 400px;
  background-color: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).toString()};

  @media ${KIOSK_PRO_MEDIA.maxWidth} {
    width: 320px;
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportLandscapeOnly} {
    width: 280px;
  }

  @media (orientation: portrait) {
    width: 100%;
  }
`;

// Off the Concierge route (every other Pro page, and every store with the rollout off) this is
// plain content padding. $isConciergeBox swaps in the exact-viewport box the Concierge page
// needs; it is driven by the same gate as the chrome theme. In portrait both clear exactly the
// ConsumerInfo panel docked at the bottom edge (KIOSK_PRO_PORTRAIT_PANEL_HEIGHT).
export const TwoColumnContent = styled.div<{ $isConciergeBox?: boolean }>`
  ${({ $isConciergeBox }) =>
    $isConciergeBox
      ? css`
          /* Chrome-offset contract for full-height embeds (the concierge page): the
             visible box is 100dvh minus these two vars. The padding below derives from
             the same vars, so the exported box and the real chrome clearance cannot
             drift. border-box keeps that padding inside the 100dvh box. No frame: in
             landscape the box meets the sidebar and the screen edges. 0px, not 0: the
             host subtracts these inside calc(). */
          --kiosk-pro-top-offset: 0px;
          --kiosk-pro-footer-height: 0px;
          box-sizing: border-box;
          padding: var(--kiosk-pro-top-offset) 0 var(--kiosk-pro-footer-height);
        `
      : css`
          padding: 10px;
        `}
  flex: 1 0 0%;
  min-width: 0;
  min-height: 100vh;
  ${({ $isConciergeBox }) =>
    $isConciergeBox &&
    css`
      min-height: 100dvh;
    `}
  --kiosk-pro-content-width: 100%;

  .two-column-content {
    padding: 26px 0 0;
  }

  .embla__viewport {
    width: calc(100% + 52px);
    margin-left: -26px;

    @media (min-width: 770px) {
      width: 100%;
      margin-left: 0;
    }
  }

  @media (orientation: portrait) {
    ${({ $isConciergeBox }) =>
      $isConciergeBox
        ? css`
            /* The fixed nav's measured height (set inline by the layout), so the box starts
               at its bottom edge. The literal applies until it is measured. */
            --kiosk-pro-top-offset: var(--kiosk-pro-nav-height, 250px);
            --kiosk-pro-footer-height: ${KIOSK_PRO_PORTRAIT_PANEL_HEIGHT.portrait};
          `
        : css`
            padding-bottom: ${KIOSK_PRO_PORTRAIT_PANEL_HEIGHT.portrait};
            padding-top: 250px;
          `}
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    ${({ $isConciergeBox }) =>
      $isConciergeBox
        ? css`
            --kiosk-pro-top-offset: var(--kiosk-pro-nav-height, 210px);
            --kiosk-pro-footer-height: ${KIOSK_PRO_PORTRAIT_PANEL_HEIGHT.portraitMaxWidth};
          `
        : css`
            padding-bottom: ${KIOSK_PRO_PORTRAIT_PANEL_HEIGHT.portraitMaxWidth};
            padding-top: 210px;
          `}
  }

  @media ${KIOSK_PRO_MEDIA.smallViewportPortrait} {
    ${({ $isConciergeBox }) =>
      $isConciergeBox
        ? css`
            --kiosk-pro-top-offset: var(--kiosk-pro-nav-height, 180px);
            --kiosk-pro-footer-height: ${KIOSK_PRO_PORTRAIT_PANEL_HEIGHT.smallViewportPortrait};
          `
        : css`
            padding-bottom: ${KIOSK_PRO_PORTRAIT_PANEL_HEIGHT.smallViewportPortrait};
            padding-top: 180px;
          `}
  }
`;
